// src/pages/ProjectsPage.tsx

import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProjects } from '@/hooks/useProjects';
import { getPlanFeatures } from '@/lib/planFeatures';
import { getActiveProjectCount } from '@/lib/projects';
import { ProjectCard } from '@/components/ProjectCard';
import { ProjectModal } from '@/components/ProjectModal';
import { UpgradeModal } from '@/components/UpgradeModal';
import type { Project } from '@/types';
import { Lock } from 'lucide-react';

export const ProjectsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const features = getPlanFeatures(user);
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'completed' | 'all'>('active');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [activeProjectCount, setActiveProjectCount] = useState(0);

  const {
    projects,
    clients,
    isLoading,
    isProcessing,
    createProject,
    updateProject,
    deleteProject,
  } = useProjects(statusFilter);

  // アクティブプロジェクト数を取得
  useEffect(() => {
    if (!user) return;
    
    getActiveProjectCount(user.uid).then(count => {
      setActiveProjectCount(count);
    });
  }, [user, projects]);

  // 制限チェック
  const maxProjects = features.maxActiveProjects;
  const isProjectLimitReached = maxProjects !== 'unlimited' && activeProjectCount >= maxProjects;

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">ログインしてください</p>
        </div>
      </div>
    );
  }

  // 制限チェック付きの新規作成
  const handleCreateProject = () => {
    if (isProjectLimitReached) {
      setShowUpgradeModal(true);
      return;
    }
    setEditingProject(null);
    setIsModalOpen(true);
  };

  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!window.confirm('このプロジェクトを削除しますか？')) {
      return;
    }

    try {
      await deleteProject(projectId);
    } catch (error: any) {
      alert(error.message || '削除に失敗しました');
    }
  };

  const handleSubmit = async (data: Partial<Project>) => {
    try {
      if (editingProject) {
        await updateProject(editingProject.id, data);
      } else {
        await createProject(data as Omit<Project, 'id' | 'userId' | 'createdAt' | 'updatedAt'>);
      }
      setIsModalOpen(false);
      setEditingProject(null);
    } catch (error) {
      console.error('Error saving project:', error);
      alert('保存に失敗しました');
    }
  };

  const filteredProjects = projects.filter((project) =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">プロジェクト管理</h1>
            <p className="text-gray-600 mt-2">プロジェクトの作成と管理</p>
            
            {/* 制限表示 */}
            {maxProjects !== 'unlimited' && (
              <p className="text-sm text-gray-500 mt-1">
                アクティブプロジェクト: {activeProjectCount} / {maxProjects}
                {isProjectLimitReached && (
                  <span className="ml-2 text-amber-600 font-medium">（上限に達しています）</span>
                )}
              </p>
            )}
          </div>
          
          {/* 新規作成ボタン（制限時はグレー） */}
          <button
            onClick={handleCreateProject}
            className={`flex items-center gap-2 px-6 py-3 font-medium rounded-xl active:scale-95 shadow-lg transition-all ${
              isProjectLimitReached
                ? 'bg-gray-300 text-gray-500 shadow-gray-300/30'
                : 'bg-gradient-to-r from-primary-600 to-primary-500 text-white hover:from-primary-700 hover:to-primary-600 shadow-primary-500/30'
            }`}
          >
            {isProjectLimitReached ? (
              <Lock className="w-5 h-5" />
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            )}
            新規プロジェクト
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="プロジェクトを検索..."
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-xl">
            {[
              { value: 'active', label: '進行中' },
              { value: 'completed', label: '完了' },
              { value: 'archived', label: 'アーカイブ' },
              { value: 'all', label: 'すべて' },
            ].map((status) => (
              <button
                key={status.value}
                onClick={() => setStatusFilter(status.value as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  statusFilter === status.value
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">プロジェクトがありません</h3>
          <p className="text-gray-600 mb-6">
            {searchQuery ? '検索条件に一致するプロジェクトがありません' : '最初のプロジェクトを作成しましょう'}
          </p>
          {!searchQuery && !isProjectLimitReached && (
            <button
              onClick={handleCreateProject}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 active:scale-95 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              新規プロジェクト
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="flex items-center gap-4 mb-6 text-sm text-gray-600">
            <span className="font-medium">{filteredProjects.length} 件のプロジェクト</span>
          </div>

          {/* Project Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onEdit={handleEditProject}
                onDelete={handleDeleteProject}
              />
            ))}
          </div>
        </>
      )}

      {/* Modal */}
      <ProjectModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProject(null);
        }}
        onSubmit={handleSubmit}
        project={editingProject}
        clients={clients}
        isProcessing={isProcessing}
      />

      {/* アップグレードモーダル */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="プロジェクト無制限登録"
      />
    </div>
  );
};
